// P5 multi-allergen pollen layer — MapLibre CustomLayerInterface + raw WebGL2.
//
// ONE INSTANCE PER REGION. Each PollenLayer renders a single regional dataset
// (Europe / North America / Asia) in its OWN draw pass with its OWN GL resources
// and its OWN slice geometry (EU 600x800, NA 585x260, Asia 440x320 — carried in
// `this.geom`, no longer the data.ts globals). The RegionRegistry instantiates
// one of these per region and adds them all to the same MapLibre map; MapLibre
// draws each independently, so a draw only ever binds THAT region's selected
// allergen arrays + its own mask — the texture-unit-safety property.
//
// Texture layout (plan Decision 3): one R8 TEXTURE_2D_ARRAY of depth nSlices
// (52 weekly slices, read from the manifest; each layer this region's WxH) PER
// allergen. Up to 12 allergen arrays bind to their own fragment texture units; a
// 13th unit binds this region's no-data/ocean mask. Selected allergens are fully
// GPU-resident, so scrubbing and allergen toggling are pure uniform updates with
// zero per-frame CPU/decode.
//
// Georeference (plan Decision 1, equirectangular winner): we use the proven P2
// FORWARD geographic mesh. A fine lon/lat lattice spans the source extent; each
// vertex is converted to mercator world coords and forward-projected by
// MapLibre's mainMatrix (the documented custom-layer path), and carries its
// SOURCE pixel coord computed on the CPU from the manifest affine
// (sx=a*lon+b*lat+c, sy=d*lon+e*lat+f). The fragment receives the interpolated
// source pixel, applies uOffsetCorr, samples each selected allergen's array at
// vec3(sx/600, sy/800, uSlice) with NEAREST, reduces with MAX, consults the
// mask, and recolors. This is robust to MapLibre's projection system and is
// already alignment-proven (manifest residuals ~2px); the closed-form inverse
// branch is unnecessary given the mesh path is exact-by-construction at vertices.
//
// Allergen selection + time index are pure uniform updates: setSelection() sets
// uSelectedCount + the live sampler-slot order; setSlice() sets uSlice. The only
// non-uniform work is a one-shot ~17 MB texSubImage3D upload the first time an
// allergen is selected (off the scrub hot path).

import type {
  CustomLayerInterface,
  CustomRenderMethodInput,
  Map as MLMap,
} from "maplibre-gl";
import type { Manifest } from "./manifest";
import { hexToRgb01 } from "./manifest";
import { geometryFromManifest, type SliceGeometry } from "./data";
import type { ProjId, ProjParams } from "./projections";
import { geoToPlanar } from "./projections";

type AnyGL = WebGLRenderingContext | WebGL2RenderingContext;

const MAX_SELECTED = 12; // sampler array size in the shader
const NX = 160; // mesh columns across the extent
const NY = 200; // mesh rows

const VERT = `#version 300 es
precision highp float;
in vec2 a_merc;   // mercator world coords [0,1]
in vec2 a_src;    // source pixel coords (px), pre-offset
uniform mat4 u_matrix;
out vec2 v_src;
void main() {
  v_src = a_src;
  gl_Position = u_matrix * vec4(a_merc, 0.0, 1.0);
}`;

function makeFrag(maxSel: number): string {
  return `#version 300 es
precision highp float;
precision highp sampler2DArray;
in vec2 v_src;                 // interpolated source pixel coords (px)
out vec4 fragColor;

uniform sampler2DArray uAllergen[${maxSel}];
uniform sampler2D uMask;       // hi-res land mask R8: 1.0 (255) = ocean/no-data
uniform int  uSelectedCount;   // how many of uAllergen[] are live
uniform int  uSlice;           // current time-slice layer index [0,35]
uniform vec4 uOffsetCorr;      // (dx, dy, sxScale, syScale) residual nudge
uniform float uRot;            // optional residual rotation (radians)
uniform vec2  uDims;           // (600, 800)
uniform int   uPaletteMode;    // 0 product (semi-transparent) / 1 debug (opaque)
uniform int   uPaintAll;       // debug: 1 = skip the level<0.5 (none) discard so
                               // the visible cut-out edge becomes the pure mask
                               // coastline for pixel-exact registration checks.
uniform vec3  uPalette[5];     // level 0..4 RGB (0..1)
uniform float uOpacity;        // product fill opacity

// Sample one allergen array layer and recover its discrete level 0..4.
// (NEAREST sampling is configured on the texture; raw in {0,64,128,192,255}.)
float levelAt(sampler2DArray t, vec3 uvw) {
  float raw = texture(t, uvw).r;           // 0..1
  return floor(raw * 255.0 / 64.0 + 0.5);  // -> {0,1,2,3,4}
}

void main() {
  // Apply the residual offset correction (scale about centre, rotate, translate)
  // on top of the fitted equirectangular source coords.
  vec2 c = uDims * 0.5;
  vec2 s = v_src - c;
  s *= vec2(uOffsetCorr.z, uOffsetCorr.w);
  float cr = cos(uRot), sr = sin(uRot);
  s = mat2(cr, -sr, sr, cr) * s;
  vec2 src = s + c + uOffsetCorr.xy;

  if (src.x < 0.0 || src.x > uDims.x || src.y < 0.0 || src.y > uDims.y) discard;

  vec2 uv = src / uDims;          // 0..1 (row 0 = TOP, matches upload)
  vec3 uvw = vec3(uv, float(uSlice));

  // Ocean / no-data: render transparent so the basemap shows through.
  if (texture(uMask, uv).r > 0.5) discard;

  // MAX over the selected allergens at the current time slice.
  float level = 0.0;
  // Unrolled, guarded by uSelectedCount (GLSL needs constant sampler indices).
  ${Array.from({ length: maxSel }, (_, i) =>
    `if (${i} < uSelectedCount) level = max(level, levelAt(uAllergen[${i}], uvw));`,
  ).join("\n  ")}

  if (uSelectedCount == 0) discard;
  // Production: level 0 (none) is transparent. Debug (uPaintAll): KEEP the
  // no-data/ocean discard above but PAINT level 0 with a flat sentinel so the
  // cut-out edge is the mask coastline (qa/offset-fix.md §4). Default off.
  if (uPaintAll == 0 && level < 0.5) discard;

  int li = int(level + 0.5);
  vec3 rgb = uPalette[0];
  ${[1, 2, 3, 4]
    .map((i) => `if (li == ${i}) rgb = uPalette[${i}];`)
    .join("\n  ")}

  if (uPaletteMode == 1) {
    // debug: exact source canonical palette, full opacity (offset compare)
    fragColor = vec4(rgb, 1.0);
  } else {
    // product: semi-transparent fill (premultiplied; MapLibre default blend)
    float a = uOpacity;
    fragColor = vec4(rgb * a, a);
  }
}`;
}

function lonLatToMerc(lon: number, lat: number): [number, number] {
  const x = (lon + 180) / 360;
  const s = Math.sin((lat * Math.PI) / 180);
  const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  return [x, y];
}

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    throw new Error("shader compile: " + gl.getShaderInfoLog(sh));
  }
  return sh;
}

interface ResidentAllergen {
  index: number; // allergen index in manifest.allergens
  tex: WebGLTexture; // N_SLICES-layer R8 array (52 weekly)
  lastUsed: number; // for LRU eviction
}

export class PollenLayer implements CustomLayerInterface {
  // Per-region layers each get a unique id ("pollen", "pollen-na", ...) so
  // several can coexist on the one MapLibre map (one draw pass each).
  id = "pollen";
  type = "custom" as const;
  renderingMode = "2d" as const;

  // Per-region slice geometry (was the data.ts globals). Each region can differ:
  // EU 600x800, NA 585x260, Asia 440x320 — all coexist on the same map.
  private geom: SliceGeometry;
  /** Base URL this region's assets are fetched from (e.g. "./data/na/"). */
  readonly base: string;

  private gl!: WebGL2RenderingContext;
  private map!: MLMap;
  private program!: WebGLProgram;
  private vao!: WebGLVertexArrayObject;
  private idxCount = 0;
  private maskTex!: WebGLTexture;
  private maskAllocated = false;
  private maskW: number;
  private maskH: number;
  // Retained CPU copy of the mask bytes so the mask texture can be re-uploaded
  // after a WebGL context loss/restore (M1) without re-fetching/decoding.
  private maskBytes: Uint8Array | null = null;
  // 1-layer dummy 2D-ARRAY bound to unused allergen sampler slots so every
  // sampler2DArray uniform references a valid array-texture unit (avoids the
  // "two textures of different types use the same sampler location" error that
  // makes the whole draw a no-op when a sampler2DArray points at a 2D unit).
  private dummyArr!: WebGLTexture;
  private readonly MASK_UNIT = 12;
  private readonly DUMMY_UNIT = 13;

  private uMatrix: WebGLUniformLocation | null = null;
  private uMask: WebGLUniformLocation | null = null;
  private uSelectedCount: WebGLUniformLocation | null = null;
  private uSlice: WebGLUniformLocation | null = null;
  private uOffsetCorr: WebGLUniformLocation | null = null;
  private uRot: WebGLUniformLocation | null = null;
  private uDims: WebGLUniformLocation | null = null;
  private uPaletteMode: WebGLUniformLocation | null = null;
  private uPaintAll: WebGLUniformLocation | null = null;
  private uOpacity: WebGLUniformLocation | null = null;
  private uAllergenLoc: (WebGLUniformLocation | null)[] = [];

  // Resident GPU arrays keyed by allergen index.
  private resident = new Map<number, ResidentAllergen>();
  // Decoded CPU copies keyed by allergen index (kept for instant re-upload).
  private cpuCache = new Map<number, Uint8Array>();
  private useCounter = 0;

  // Current selection (ordered list of allergen indices, max 12).
  private selection: number[] = [];
  private slice = 0;
  private paletteMode = 0;
  // Debug flag (default off, production unchanged). When on, the fragment skips
  // the level<0.5 (none) discard but keeps the no-data discard — see makeFrag.
  private paintAll = 0;
  // C2 fix: lower default fill alpha so the basemap geography/labels show
  // through every category (was 0.62; see qa/fix-spec-c1c2.md).
  private opacity = 0.45;
  private maxResident = 12;

  private palette01: [number, number, number][];

  constructor(
    private manifest: Manifest,
    maxResident: number,
    opts: { id?: string; base?: string } = {},
  ) {
    if (opts.id) this.id = opts.id;
    this.base = opts.base ?? manifest.dataDir ?? "./data/";
    this.geom = geometryFromManifest(manifest);
    this.maskW = this.geom.width;
    this.maskH = this.geom.height;
    this.maxResident = Math.max(1, Math.min(12, maxResident));
    this.palette01 = manifest.palette.levels
      .slice()
      .sort((a, b) => a.level - b.level)
      .map((l) => hexToRgb01(l.color));
  }

  /** Per-region slice geometry (read-only, used by the registry's decoder). */
  get geometry(): SliceGeometry {
    return this.geom;
  }

  /** Update the per-region LRU residency cap (e.g. after the caps decision). */
  setMaxResident(n: number): void {
    this.maxResident = Math.max(1, Math.min(12, n));
  }

  onAdd(map: MLMap, glIn: AnyGL) {
    this.map = map;
    this.gl = glIn as WebGL2RenderingContext;
    const gl = this.gl;

    // M1 (context loss): a re-add on a freshly-restored GL context must rebuild
    // every GPU object from scratch. The old handles (program/VAO/textures) are
    // invalid on the new context, so drop our bookkeeping — the mask is
    // re-uploaded from the retained CPU bytes and selected allergens are
    // re-uploaded from cpuCache by restoreAfterContextLoss(). CPU caches
    // (cpuCache, maskBytes, selection, slice) are preserved across the loss.
    this.maskAllocated = false;
    this.resident.clear();

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, makeFrag(MAX_SELECTED));
    this.program = gl.createProgram()!;
    gl.attachShader(this.program, vs);
    gl.attachShader(this.program, fs);
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      throw new Error("link: " + gl.getProgramInfoLog(this.program));
    }

    this.uMatrix = gl.getUniformLocation(this.program, "u_matrix");
    this.uMask = gl.getUniformLocation(this.program, "uMask");
    this.uSelectedCount = gl.getUniformLocation(this.program, "uSelectedCount");
    this.uSlice = gl.getUniformLocation(this.program, "uSlice");
    this.uOffsetCorr = gl.getUniformLocation(this.program, "uOffsetCorr");
    this.uRot = gl.getUniformLocation(this.program, "uRot");
    this.uDims = gl.getUniformLocation(this.program, "uDims");
    this.uPaletteMode = gl.getUniformLocation(this.program, "uPaletteMode");
    this.uPaintAll = gl.getUniformLocation(this.program, "uPaintAll");
    this.uOpacity = gl.getUniformLocation(this.program, "uOpacity");
    for (let i = 0; i < MAX_SELECTED; i++) {
      this.uAllergenLoc[i] = gl.getUniformLocation(
        this.program,
        `uAllergen[${i}]`,
      );
    }

    this.buildMesh(gl);
    // Mask texture unit 12 always bound; allergen samplers use units 0..11.
    // The mask is the hi-res transparency/land mask (C1 fix); its storage is
    // allocated lazily in setMask() once its dimensions are known (texStorage2D
    // is immutable / single-shot, and the hi-res mask can be 3600x4800).
    this.maskTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.maskTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // Dummy 1x1 single-layer R8 array for unused sampler2DArray slots.
    this.dummyArr = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.dummyArr);
    gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, gl.R8, 1, 1, 1);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  }

  /**
   * Upload the transparency/land mask (called once, after decode). Accepts the
   * mask dimensions so the hi-res mask (e.g. 3600x4800, C1 fix) or the legacy
   * 600x800 mask can both be used. Sampled NEAREST so coastlines stay crisp at
   * any zoom. Storage is allocated immutably on first call.
   */
  setMask(mask: Uint8Array, width = this.geom.width, height = this.geom.height) {
    // Retain the bytes + dims so the mask can be re-uploaded after a context
    // restore (M1) without re-decoding.
    this.maskBytes = mask;
    this.maskW = width;
    this.maskH = height;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.maskTex);
    if (!this.maskAllocated) {
      gl.texStorage2D(gl.TEXTURE_2D, 1, gl.R8, width, height);
      this.maskW = width;
      this.maskH = height;
      this.maskAllocated = true;
    }
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texSubImage2D(
      gl.TEXTURE_2D, 0, 0, 0, this.maskW, this.maskH,
      gl.RED, gl.UNSIGNED_BYTE, mask,
    );
  }

  /**
   * M1 — re-upload all resident GPU resources after a WebGL context restore.
   * Call this AFTER the layer has been re-added (onAdd ran on the fresh
   * context, recreating program/VAO/mask+dummy textures and clearing the
   * resident map). Re-uploads the retained mask bytes and re-makes every
   * selected allergen's array texture resident from its retained CPU copy, then
   * repaints. Safe to call when nothing was selected (mask-only restore).
   */
  restoreAfterContextLoss() {
    if (!this.gl) return;
    if (this.maskBytes) {
      this.setMask(this.maskBytes, this.maskW, this.maskH);
    }
    for (const idx of this.selection) {
      if (this.cpuCache.has(idx)) this.ensureResident(idx);
    }
    if (this.map) this.map.triggerRepaint();
  }

  private buildMesh(gl: WebGL2RenderingContext) {
    const A = this.manifest.geo.params.affine;
    const a = A[0][0], b = A[0][1], c = A[0][2];
    const d = A[1][0], e = A[1][1], f = A[1][2];
    const cor = this.manifest.geo.bbox.corners;
    const lons = [
      cor.top_left.lon, cor.top_right.lon,
      cor.bottom_right.lon, cor.bottom_left.lon,
    ];
    const lats = [
      cor.top_left.lat, cor.top_right.lat,
      cor.bottom_right.lat, cor.bottom_left.lat,
    ];
    const lonMin = Math.min(...lons), lonMax = Math.max(...lons);
    const latMin = Math.min(...lats), latMax = Math.max(...lats);

    // Projection-aware planar mapping (shared with the calibration tool +
    // pipeline/georef/models.py). For uProjModel=0 (equirect) geoToPlanar
    // returns [lon,lat], so sx=a*lon+b*lat+c — byte-identical to the previous
    // hard-coded equirect path. Any other family (mercator/LCC/LAEA/stereo) now
    // renders with no further code change once its geo block is in the manifest.
    const projId = (this.manifest.geo.params.uProjModel ?? 0) as ProjId;
    const projParams = this.manifest.geo.params as ProjParams;

    const verts: number[] = []; // merc.x, merc.y, src.x, src.y
    for (let j = 0; j <= NY; j++) {
      const lat = latMax - (j / NY) * (latMax - latMin);
      for (let i = 0; i <= NX; i++) {
        const lon = lonMin + (i / NX) * (lonMax - lonMin);
        const [mx, my] = lonLatToMerc(lon, lat);
        const [X, Y] = geoToPlanar(projId, projParams, lon, lat);
        const sx = a * X + b * Y + c;
        const sy = d * X + e * Y + f;
        verts.push(mx, my, sx, sy);
      }
    }
    const idx: number[] = [];
    const stride = NX + 1;
    for (let j = 0; j < NY; j++) {
      for (let i = 0; i < NX; i++) {
        const p = j * stride + i;
        idx.push(p, p + 1, p + stride, p + 1, p + stride + 1, p + stride);
      }
    }
    this.idxCount = idx.length;

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.STATIC_DRAW);
    const aMerc = gl.getAttribLocation(this.program, "a_merc");
    const aSrc = gl.getAttribLocation(this.program, "a_src");
    gl.enableVertexAttribArray(aMerc);
    gl.vertexAttribPointer(aMerc, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(aSrc);
    gl.vertexAttribPointer(aSrc, 2, gl.FLOAT, false, 16, 8);
    const ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(idx), gl.STATIC_DRAW,
    );
    gl.bindVertexArray(null);
  }

  /** True if this allergen's decoded CPU copy is already cached. */
  hasCpu(index: number): boolean {
    return this.cpuCache.has(index);
  }

  /** Provide a freshly-decoded CPU copy (does not upload yet). */
  setCpu(index: number, data: Uint8Array) {
    this.cpuCache.set(index, data);
  }

  /** Ensure an allergen's N_SLICES-layer array is GPU-resident (one-shot upload). */
  private ensureResident(index: number) {
    if (this.resident.has(index)) {
      this.resident.get(index)!.lastUsed = ++this.useCounter;
      return;
    }
    const data = this.cpuCache.get(index);
    if (!data) throw new Error(`allergen ${index} not decoded yet`);

    // LRU eviction if at the residency cap.
    while (this.resident.size >= this.maxResident) {
      let lruIdx = -1, lru = Infinity;
      for (const [k, v] of this.resident) {
        // Never evict something in the current selection.
        if (this.selection.includes(k)) continue;
        if (v.lastUsed < lru) { lru = v.lastUsed; lruIdx = k; }
      }
      if (lruIdx < 0) break; // everything resident is selected; cannot evict
      const victim = this.resident.get(lruIdx)!;
      this.gl.deleteTexture(victim.tex);
      this.resident.delete(lruIdx);
    }

    const gl = this.gl;
    const { width: W, height: H, nSlices: N, sliceBytes: BYTES } = this.geom;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, tex);
    gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, gl.R8, W, H, N);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    for (let s = 0; s < N; s++) {
      const layer = data.subarray(s * BYTES, (s + 1) * BYTES);
      gl.texSubImage3D(
        gl.TEXTURE_2D_ARRAY, 0, 0, 0, s, W, H, 1,
        gl.RED, gl.UNSIGNED_BYTE, layer,
      );
    }
    this.resident.set(index, { index, tex, lastUsed: ++this.useCounter });
  }

  /** Set the ordered selection (allergen indices). CPU copies must exist. */
  setSelection(indices: number[]) {
    this.selection = indices.slice(0, MAX_SELECTED);
    for (const idx of this.selection) this.ensureResident(idx);
    if (this.map) this.map.triggerRepaint();
  }

  setSlice(slice: number) {
    this.slice = Math.max(0, Math.min(this.geom.nSlices - 1, Math.round(slice)));
    if (this.map) this.map.triggerRepaint();
  }

  setPaletteMode(mode: 0 | 1) {
    this.paletteMode = mode;
    if (this.map) this.map.triggerRepaint();
  }

  /**
   * Debug: when on, the fragment shader paints level-0 ("none") cells instead
   * of discarding them, KEEPING the no-data/ocean discard. The visible cut-out
   * edge then traces the pure no-data MASK coastline, which makes the live
   * coastline-registration check pixel-exact (qa/offset-fix.md §4). Default off;
   * the production render path is unchanged.
   */
  setPaintAll(on: boolean) {
    this.paintAll = on ? 1 : 0;
    if (this.map) this.map.triggerRepaint();
  }

  setOpacity(o: number) {
    this.opacity = o;
    if (this.map) this.map.triggerRepaint();
  }

  render(glIn: AnyGL, args: CustomRenderMethodInput) {
    const gl = glIn as WebGL2RenderingContext;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);

    gl.uniformMatrix4fv(
      this.uMatrix, false,
      Array.from(args.defaultProjectionData.mainMatrix) as number[],
    );

    // Bind mask (sampler2D) on its own unit, and the dummy array (sampler2DArray)
    // on another, so sampler TYPES never collide on a unit.
    gl.activeTexture(gl.TEXTURE0 + this.MASK_UNIT);
    gl.bindTexture(gl.TEXTURE_2D, this.maskTex);
    gl.uniform1i(this.uMask, this.MASK_UNIT);
    gl.activeTexture(gl.TEXTURE0 + this.DUMMY_UNIT);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.dummyArr);

    // Bind selected allergen arrays to units 0..count-1, in selection order.
    const count = this.selection.length;
    for (let i = 0; i < count; i++) {
      const r = this.resident.get(this.selection[i]);
      if (!r) {
        // Resource not (yet) resident — e.g. a frame between a context restore
        // and restoreAfterContextLoss() re-uploading. Point the slot at the
        // dummy ARRAY unit so the sampler stays valid (no stale/cross-type
        // binding) and this slot contributes level 0 to the MAX. (M1)
        gl.uniform1i(this.uAllergenLoc[i], this.DUMMY_UNIT);
        continue;
      }
      r.lastUsed = ++this.useCounter;
      gl.activeTexture(gl.TEXTURE0 + i);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, r.tex);
      gl.uniform1i(this.uAllergenLoc[i], i);
    }
    // Point unused sampler2DArray slots at the dummy ARRAY unit (matching type).
    for (let i = count; i < MAX_SELECTED; i++) {
      gl.uniform1i(this.uAllergenLoc[i], this.DUMMY_UNIT);
    }

    gl.uniform1i(this.uSelectedCount, count);
    gl.uniform1i(this.uSlice, this.slice);
    gl.uniform1i(this.uPaletteMode, this.paletteMode);
    gl.uniform1i(this.uPaintAll, this.paintAll);
    gl.uniform1f(this.uOpacity, this.opacity);
    gl.uniform2f(this.uDims, this.geom.width, this.geom.height);

    const oc = this.manifest.geo.offset_correction;
    gl.uniform4f(this.uOffsetCorr, oc.dx, oc.dy, oc.sx_scale, oc.sy_scale);
    gl.uniform1f(this.uRot, oc.rot);

    const flat = new Float32Array(5 * 3);
    for (let i = 0; i < 5; i++) {
      flat[i * 3] = this.palette01[i][0];
      flat[i * 3 + 1] = this.palette01[i][1];
      flat[i * 3 + 2] = this.palette01[i][2];
    }
    const uPal = gl.getUniformLocation(this.program, "uPalette");
    gl.uniform3fv(uPal, flat);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied
    gl.drawElements(gl.TRIANGLES, this.idxCount, gl.UNSIGNED_INT, 0);
    gl.bindVertexArray(null);
  }

  onRemove() {
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteVertexArray(this.vao);
    gl.deleteTexture(this.maskTex);
    gl.deleteTexture(this.dummyArr);
    for (const r of this.resident.values()) gl.deleteTexture(r.tex);
    this.resident.clear();
  }
}
